import React, { useState, useEffect } from 'react';
import '../css/characterSelector.css';

import LRUCache from '../components/cache.js';

const cookieCache = new LRUCache(30);

const CookieWrapper = ({ getCookieData: passCookieData }) => {
  const [cookieName, setCookieName] = useState("Dark_Choco_Cookie");
  const [starCount, setStarCount] = useState(5);
  const [skillLevel, setSkillLevel] = useState(0);
  const [affectionLevel, setAffectionLevel] = useState(0);
  const [cookieData, setCookieData] = useState(null);
  const [cookieList, setCookieList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState("Release");


  const skillMax = 12
  const affectionMax = 3

  // Get details of selected cookie
  async function fetchSelectedCookie(cookiename, stars, skillLevel, affection) {

    const cacheKey = JSON.stringify({cookiename, stars, skillLevel, affection});
    const cached = cookieCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const res = await fetch('/api/cookie/getCookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cacheKey,
      });

      if (!res.ok) {
        console.error('Failed to fetch cookie data');
        return null;
      }

      const data = await res.json();
      cookieCache.set(cacheKey, data);
      return data;
    } catch (err) {
      console.error('Error fetching cookie:', err);
      return null;
    }
  }


  // Get list of all cookies
  async function fetchAllCookies() {
    const res = await fetch('/api/cookie/getData', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      console.error('Failed to fetch cookie list');
      return [];
    }
    return await res.json();
  }

  useEffect(() => {
    // Load list of all cookies once
    fetchAllCookies().then(setCookieList);
  }, []);

  useEffect(() => {
    // Load selected cookie data
    fetchSelectedCookie(cookieName, starCount, skillLevel, affectionLevel).then(data => {
      if (data) setCookieData(data);
    });
  }, [cookieName, starCount, skillLevel, affectionLevel]);

  const grabImg = (folder, cookie, imgName) => {
    const path = cookie.replace(/ /g, "_");
    return `/img/${folder}/${path}/${imgName}.webp`;
  };

  const updateStarCount = (newCount) => {
    const clamped = Math.max(1, Math.min(5, newCount));
    setStarCount(clamped);
  };
  const updateSkillLevel = (newCount) => {
    let clamped = Math.max(0, Math.min(skillMax, newCount));
    if (clamped === skillLevel) {
      clamped = 0
    }
    setSkillLevel(clamped);
  };
  const updateAffectionLevel = (newCount) => {
    let clamped = Math.max(0, Math.min(affectionMax, newCount));
    if (clamped === affectionLevel) {
      clamped = 0
    }
    setAffectionLevel(clamped);
  };


  const getSortedCookies = () => {
  //Sort cookies by release
  const entries = Object.entries(cookieList).sort(
    (a, b) => b[1].id - a[1].id
  );

  switch (sortMethod) {
    case "Element":
      return groupBy(entries, ([, c]) => c.element);
    case "Class":
      return groupBy(entries, ([, c]) => c.class);
    case "Role":
      return groupBy(entries, ([, c]) => c.role);
    case "Rarity":
      return groupBy(entries, ([, c]) => c.rarity);
    case "Release":
      return { All: entries };
    default:
      return { All: entries };
    }
  };

  // Group array of [key, cookie] into an object keyed by grouping value
  function groupBy(array, keyFn) {
    return array.reduce((acc, item) => {
      const key = keyFn(item) || "Unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }


  useEffect(() => { 
      passCookieData(cookieData);
  }, [cookieData]) 

  return (
    <span className="characterWrapper">
      {/* Display if cookie data is available */}
      {cookieData ? (
        <>
          {/* cookie name and image */}
        <div className={`characterContainer rarityBgSoft rarityBorder ${cookieData.rarity.toLowerCase()}`}>
          <div className={`characterHeader rarityBorder ${cookieData.rarity.toLowerCase()}`}>Cookie</div>
          <div className="selectedName">{cookieData.name}</div>
          <img
            src={grabImg("Cookie", cookieData.name, "profile")}
            alt={cookieData.name}
            className={`cookieImage rarityBorder rairtyBg ${cookieData.rarity.toLowerCase()}`}
            onClick={() => setDropdownOpen(true)} // Open modal on image click
            style={{ cursor: 'pointer' }}
          />
          {/* Star display and increments */}
          <div className="starControls">
            <button onClick={() => updateStarCount(starCount - 1)}>−</button>
            {[...Array(5)].map((_, index) => (
              <img 
                src="/img/Icons/Other/Star.webp"
                key={index}
                alt="star"
                className={`star ${index < starCount ? 'filled' : 'empty'}`}
              />
            ))}
            <button onClick={() => updateStarCount(starCount + 1)}>+</button>
          </div>
          <div className="characterSelectorLabel">
              Stars: {starCount}/5
          </div>

          {/* Affection display and increments */}
          <div className="affectionControls">
            <button onClick={() => updateAffectionLevel(affectionLevel - 1)}>−</button>
            {[...Array(affectionMax)].map((_, index) => (
              <img 
                src="/img/Icons/Other/Heart.webp"
                key={index}
                alt="heart"
                className={`heart ${index < affectionLevel ? 'filled' : 'empty'}`}
              />
            ))}
            <button onClick={() => updateAffectionLevel(affectionLevel + 1)}>+</button>
          </div>
          <div className="characterSelectorLabel">
              Affection buffs: {affectionLevel}/{affectionMax} <br/>
              (At least affection level {affectionLevel * 3})
          </div>
        {/* Skill Level Display */}
        {skillMax !== undefined && (
          <div className="skillLevelContainer">
            <div className="skillBar">
              {[...Array(skillMax)].map((_, index) => (
                <div
                  key={index}
                  className={`skillSegment ${index < skillLevel ? 'filled' : ''}`}
                  onClick={() => updateSkillLevel(index + 1)}
                />
              ))}
            </div>
            <div className="characterSelectorLabel">
              Skill Lv. {skillLevel}/{skillMax}
            </div>
          </div>
        )}
        </div>
        </>
      ) : (
        "Loading..."
      )}


      {/* Cookie selection modal */}
      {dropdownOpen && (
        <div className="cookieModalOverlay" onClick={() => setDropdownOpen(false)}>
          <div className="cookieModalContent" onClick={(e) => e.stopPropagation()}>
            
            {/* Sorting Control inside modal */}
            <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
              <label> Group By:</label>
              <select
                value={sortMethod}
                onChange={(e) => setSortMethod(e.target.value)}
                style={{
                  background: "#222",
                  color: "#fff",
                  border: "1px solid #555",
                  borderRadius: "4px",
                  padding: "4px"
                }}
              >
                <option value="Rarity">Rarity</option>
                <option value="Element">Element</option>
                <option value="Class">Class</option>
                <option value="Role">Role</option>
                <option value="Release">No Group</option>
              </select>
            </div>

            {/* Render grouped cookies */}
            {Object.entries(getSortedCookies()).map(([group, items]) => (
              <div key={group} style={{ width: "100%" }}>
                {sortMethod !== "Release" && (
                  <div className='groupLabel'>
                    <img src={
                      grabImg("Icons", sortMethod, group)} 
                      className={`groupIcon ${sortMethod}`}
                      alt={group}>
                    </img>
                    {sortMethod !== "Rarity" && group}
                    </div>
                )}
                <div className={`${sortMethod !== "Release" ? "categoryGridCookie" : "gridCookie"}`}>
                  {items.map(([key, cookie]) => (
                    <div
                      key={key}
                      className="cookieImageWrapper"
                      style={{
                        border: key === cookieName ? "2px solid green" : "0px white",
                        borderRadius: "6px"
                      }}
                    >
                      <img
                        src={grabImg("Cookie", key, "profile")}
                        alt={cookie.name}
                        className={`dropdownImage rarityBg rarityBorder ${cookie.rarity?.toLowerCase()}`}
                        onClick={() => {
                          setCookieName(key);
                          setDropdownOpen(false);
                        }}
                      />
                      <div className="nameTooltip" style={{ fontSize: "0.8rem", textAlign: "center" }}>
                        {cookie.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </span>
  );
};

export default CookieWrapper;
